/**
 * Landing page for the link `lib/auth.ts`'s `sendResetPassword` callback
 * emails out — triggered by the "Send password reset email" button on
 * `PortalAccessCard.tsx`. Posts straight to Better Auth's own
 * `POST /api/auth/reset-password` (`{ newPassword, token }`), same as
 * `session.tsx`'s `signIn` posts to `/auth/sign-in/email` — this is Better
 * Auth's endpoint, not one of ours, so the response is a bare `{status}`
 * with no session cookie. A successful reset does not sign the person in;
 * it revokes their other sessions (`revokeSessionsOnPasswordReset`), so
 * they sign in fresh with the new password from here.
 *
 * `token` comes off the query string, not the path — matches the URL
 * `sendResetPassword` builds (`/reset-password?token=...`), deliberately
 * skipping Better Auth's own `/api/auth/reset-password/:token` redirect
 * hop (see that callback's own comment).
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, ApiError } from '../../lib/api.js';
import { useBrand } from '../../lib/brand.js';
import { Field, PasswordInput } from '../../components/Form.js';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { product } = useBrand();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await api('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ newPassword: password, token }),
      });
      setDone(true);
    } catch (caught) {
      // Better Auth's error body doesn't share our app's {error:{code,message}}
      // shape, so api() falls back to a generic message here — no attempt to
      // distinguish "expired" from "already used" from "malformed", same
      // one-message posture LoginPage takes for bad credentials.
      setError(
        caught instanceof ApiError && caught.status === 429
          ? 'Too many attempts. Wait a minute and try again.'
          : 'This link is invalid or has expired. Ask for a new one.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="auth-page">
        <div className="card card--auth card--auth--portal">
          <h1>Reset password</h1>
          <p className="alert alert--error">This link is missing its token.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="auth-page">
        <div className="card card--auth card--auth--portal">
          <h1>Password updated</h1>
          <p className="muted">Sign in with your new password.</p>
          <a className="button" href="/login">
            Sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="card card--auth card--auth--portal">
        <h1>Reset your {product.name} password</h1>
        <p className="muted">Choose a new password below.</p>

        {error ? (
          <p className="alert alert--error" role="alert">
            {error}
          </p>
        ) : null}

        <form onSubmit={(event) => void onSubmit(event)} noValidate>
          <Field label="New password" name="password" hint="At least 12 characters." required>
            <PasswordInput
              name="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Confirm new password" name="confirm" required>
            <PasswordInput
              name="confirm"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
            />
          </Field>

          <button className="button" type="submit" disabled={busy}>
            {busy ? 'Updating…' : 'Reset password'}
          </button>
        </form>
      </div>
    </div>
  );
}
